import { fabric } from "fabric";

import CanvasHandler from "./CanvasHandler";
import { FabricObject } from "../types/FabricCustomType";
import store from "../redux/store";
import { isTouchDevice } from "../utils/InteractionUtil";

class ZoomHandler {
  canvasHandler?: CanvasHandler;
  ZoomFactor: number = 1.7;

  private static readonly DEFAULT_FIT_PADDING_FACTOR = 0.9;

  constructor(canvasHandler: CanvasHandler) {
    this.canvasHandler = canvasHandler;
  }

  /**
   * Zoom centrando el canvas en el punto dado
   * @param {fabric.Point} point - punto a centrar
   */
  public zoomToCenterAtPoint = (point: fabric.Point) => {
    const canvas = this.canvasHandler.canvas;
    const { minZoom, maxZoom } = this.canvasHandler;

    let zoomRatio = maxZoom / 100;
    if (zoomRatio <= minZoom / 100) zoomRatio = minZoom / 100;

    const canvasWidth = canvas.getWidth();
    const canvasHeight = canvas.getHeight();

    // Queremos que el punto dado quede en el centro del canvas
    const translateX = canvasWidth / 2 - point.x * zoomRatio;
    const translateY = canvasHeight / 2 - point.y * zoomRatio;

    canvas.setViewportTransform([
      zoomRatio, 0, 0,
      zoomRatio,
      translateX,
      translateY,
    ]);

    if (this.canvasHandler.onZoom) {
      this.canvasHandler.onZoom(zoomRatio);
    }
    canvas.requestRenderAll();
  };

  /**
   * Zoom to point
   *
   * @param {fabric.Point} point
   * @param {number} zoom ex) 0 ~ 1. Not percentage value.
   */
  public zoomToPoint = (point: fabric.Point, zoom: number) => {
    const canvas = this.canvasHandler.canvas;
    const vpt = canvas.viewportTransform;

    const { minZoom, maxZoom } = this.canvasHandler;

    let zoomRatio = zoom;
    if (zoom <= minZoom / 100) zoomRatio = minZoom / 100;
    if (zoom >= maxZoom / 100) zoomRatio = maxZoom / 100;

    const prevZoom = vpt[0];

    const zoomFactor = zoomRatio / prevZoom;

    vpt[4] = point.x - zoomFactor * (point.x - vpt[4]);
    vpt[5] = point.y - zoomFactor * (point.y - vpt[5]);

    vpt[0] = zoomRatio;
    vpt[3] = zoomRatio;

    canvas.setViewportTransform(vpt);

    if (this.canvasHandler.onZoom) {
      this.canvasHandler.onZoom(zoomRatio);
    }

    canvas.requestRenderAll();
  };

  /**
   * Zoom one to one
   *
   */
  public zoomOneToOne = () => {
    const center = this.canvasHandler.canvas.getCenter();
    this.zoomToPoint(new fabric.Point(center.left, center.top), 1);
  };

  /**
   * Zoom to fit
   *
   */
  public zoomToFit = (
    options?: {
      alignTop?: boolean;
      topPadding?: number;
      sidePadding?: number;
      bottomPadding?: number;
    }
  ) => {
    const canvas = this.canvasHandler.canvas;
    const objects = canvas.getObjects();
    if (!objects.length) return;

    const group = new fabric.Group(objects, {
      selectable: false,
      evented: false,
    });

    const bounds = group.getBoundingRect(true, true);
    group.destroy();

    this.canvasHandler.viewportBounds = bounds;

    const canvasWidth = canvas.getWidth();
    const canvasHeight = canvas.getHeight();

    const {
      alignTop = false,
      topPadding = 8,
      sidePadding = 16,
      bottomPadding = 8,
    } = options ?? {};

    const safeWidth = Math.max(canvasWidth - sidePadding * 2, 1);
    const safeHeight = Math.max(canvasHeight - topPadding - bottomPadding, 1);

    const zoomX = safeWidth / bounds.width;
    const zoomY = safeHeight / bounds.height;

    const fitPaddingFactor = alignTop
      ? 1
      : ZoomHandler.DEFAULT_FIT_PADDING_FACTOR;
    const zoom = Math.min(zoomX, zoomY) * fitPaddingFactor;

    // El centrado horizontal se mantiene siempre; alignTop solo afecta el eje vertical.
    const translateX = canvasWidth / 2 - (bounds.left + bounds.width / 2) * zoom;
    const translateY = alignTop
      ? topPadding - bounds.top * zoom
      : canvasHeight / 2 - (bounds.top + bounds.height / 2) * zoom;

    // reset de estado crítico
    canvas.discardActiveObject();

    canvas.setViewportTransform([
      zoom, 0, 0,
      zoom,
      translateX,
      translateY,
    ]);

    // reactivar selección
    canvas.selection = true;

    canvas.forEachObject(obj => obj.setCoords());

    if (this.canvasHandler.onZoom) {
      this.canvasHandler.onZoom(zoom);
    }

    canvas.requestRenderAll();

    if (!this.canvasHandler.editable) {
      this.canvasHandler.minZoom = canvas.getZoom() * 100;

      const {
        properties: { rowSeatSpacing, rowSeatSize },
      } = store.getState();

      const seatSize = rowSeatSize; // tamaño de cada asiento
      const seatSpacing = rowSeatSpacing // espacio entre asientos
      const seatsToShow = isTouchDevice() ? 10 : 30; // mínimo de asientos visibles
      const seatTotal = seatSize + seatSpacing;// tamaño total por asiento (incluyendo espacio)

      // El zoom máximo para que quepan al menos 20 asientos completos en pantalla:
      const maxZoom = canvasWidth / (seatsToShow * seatTotal);

      // Si necesitas el porcentaje (por ejemplo, para fabric.js que usa 1 = 100%)
      const maxZoomPercent = maxZoom * 100;

      this.canvasHandler.maxZoom = maxZoomPercent;
      this.setZoomFactor(this.canvasHandler.maxZoom, this.canvasHandler.minZoom);
    }
  };

  /**
   * Zoom in
   *
   */
  public zoomIn = () => {
    const canvas = this.canvasHandler.canvas;
    const currentZoom = canvas.getZoom();
    const center = canvas.getCenter();

    const nextZoom = currentZoom * this.ZoomFactor;

    this.zoomToPoint(
      new fabric.Point(center.left, center.top),
      nextZoom
    );
  };

  /**
   * Zoom out
   *
   */
  public zoomOut = () => {
    const canvas = this.canvasHandler.canvas;
    const currentZoom = canvas.getZoom();
    const center = canvas.getCenter();

    const nextZoom = currentZoom / this.ZoomFactor;

    this.zoomToPoint(
      new fabric.Point(center.left, center.top),
      nextZoom
    );
  };

  /**
   * Zoom to center with object
   *
   * @param {FabricObject} target If zoomFit true, rescaled canvas zoom.
   */
  public zoomToCenterWithObject = (
    target: FabricObject
  ) => {
    const canvas = this.canvasHandler.canvas;

    // Bounding box del objeto
    const bounds = target.getBoundingRect(true, true);
    this.canvasHandler.viewportBounds = bounds;

    const canvasWidth = canvas.getWidth();
    const canvasHeight = canvas.getHeight();

    // Calcular zoom
    let zoomX = canvasWidth / bounds.width;
    let zoomY = canvasHeight / bounds.height;
    let zoom = Math.min(zoomX, zoomY) * 0.9;

    // Punto de referencia
    const centerPoint = new fabric.Point(
      bounds.left + bounds.width / 2,
      bounds.top + bounds.height / 2
    );

    // Calcular translate
    const translateX =
      canvasWidth / 2 - centerPoint.x * zoom;
    const translateY =
      canvasHeight / 2 - centerPoint.y * zoom;

    // Aplicar viewport
    canvas.setViewportTransform([
      zoom, 0, 0,
      zoom,
      translateX,
      translateY
    ]);

    canvas.requestRenderAll();
  };


  private setZoomFactor(maxZoom: number, minZoom: number): void {
    // Queremos que (minZoom * ZoomFactor^3) >= maxZoom
    // ZoomFactor = (maxZoom / minZoom)^(1/3)
    if (minZoom <= 0) {
      this.ZoomFactor = 1.7;
      return;
    }
    const ratio = maxZoom / minZoom;
    let factor = Math.pow(ratio, 1 / 2);
    if (factor < 1.7) factor = 1.7;
    this.ZoomFactor = factor;
  }
}


export default ZoomHandler;
